import { InfiniteData } from '@tanstack/react-query';

import { PaginatedResponse } from '@/modules/common/types/pagination-types';

export const DEFAULT_PAGE_SIZE = 25;
export const DEFAULT_FILTERS_PAGE_SIZE = 25;
export const DEFAULT_PAGE_SIZE_PROJECTS_CHANGES = DEFAULT_PAGE_SIZE;
export const DEFAULT_PAGE_SIZE_PROJECTS_SHORT_ENTITIES = DEFAULT_PAGE_SIZE;
export const DEFAULT_PAGE_SIZE_CLIENTS_SHORT_ENTITIES = DEFAULT_PAGE_SIZE;
export const DEFAULT_PAGE_SIZE_PLANNER_ASSIGNMENTS = DEFAULT_PAGE_SIZE;
export const ONBOARDING_PAGE_SIZE = 25;
export const DEFAULT_PAGE_SIZE_GEONAMES_API = 50;
export const DEFAULT_PAGE_SIZE_NOTIFICATIONS = DEFAULT_PAGE_SIZE;
export const DEFAULT_PAGE_SIZE_TIME_TRACKS = 50;
export const DEFAULT_PAGE_SIZE_APPROVALS = 50;

/**
 * Generic function to determine if there's a next page in cursor-based pagination.
 * Compares total loaded items across all pages with the total count.
 *
 * @param lastPage - The last page of results
 * @param allPages - All pages loaded so far
 * @returns The cursor for the next page, or undefined if all items are loaded
 */
export const getNextPageParam = <T>(
  lastPage: PaginatedResponse<T> | undefined,
  allPages: PaginatedResponse<T>[]
): number | null | undefined => {
  if (!lastPage) return undefined;

  const loadedItemsCount = allPages.reduce((sum, page) => sum + page.items.length, 0);
  const totalItems = lastPage.pagination?.total ?? 0;

  if (loadedItemsCount >= totalItems) {
    return undefined;
  }

  return lastPage.pagination?.cursor ?? undefined;
};

/**
 * Flattens the pages of a cursor-paginated `useInfiniteQuery` into one `PaginatedResponse`
 * (all items, last page's pagination). Pass it as `select` by reference — React Query only reuses
 * a select result while the function identity is stable, so an inline arrow would hand every
 * render a fresh `items` array and, for a table, rebuild every TanStack row.
 */
export const flattenInfinitePages = <T>(
  data: InfiniteData<PaginatedResponse<T>, number | null | undefined>
): PaginatedResponse<T> =>
  new PaginatedResponse({
    items: data.pages.flatMap(page => page.items),
    pagination: data.pages[data.pages.length - 1]?.pagination,
  });
